import { Box, Flex, Tag } from "@chakra-ui/react";
import React from "react";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { GET_SCHOOLS_RANK_VIEW } from "src/scenes/orgAdmin/forms/graphql/queries";
import { SingleTagsPopover } from "src/scenes/orgAdmin/tags/SingleTagsPopover";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import { Permission } from "src/types/permissions";
import { AdminSchoolRank } from "src/types/schoolRank";
import { WithRequiredHasuraRoles } from "../../Permissions/WithRequiredHasuraRoles";
import { WithUserPermissions } from "../../Permissions/WithUserPermissions";
import { TagGroupName } from "./TagGroupName";

type Props = {
  enrollmentPeriodId: uuid;
  tagGroups: GQL.GetSchoolsRankView_tag_group[];
  schoolRank: AdminSchoolRank;
};

export const TagGroups: React.FC<Props> = ({
  enrollmentPeriodId,
  tagGroups,
  schoolRank,
}) => {
  const flags = useFlags(["tagging-abac"]);

  const formSchoolTagPermissions: Permission[] = flags["tagging-abac"].enabled
    ? ["form.school.tag:create", "form.school.tag:update"]
    : ["form:update"];

  const groupedTags = tagGroups.map((tagGroup) => ({
    id: tagGroup.id,
    name: tagGroup.name,
    tags: schoolRank.tags.filter(
      (tag) => tag.enrollment_period_tag.tag_group_id === tagGroup.id
    ),
    isExternal: tagGroup.is_external,
  }));

  const isAvelaOrgAdmin = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
  ]);

  return (
    <Flex direction="column" gap={4} mb={2} ml={6}>
      <WithRequiredHasuraRoles
        roles={[
          HasuraRole.ADMIN,
          HasuraRole.DISTRICT_ADMIN,
          HasuraRole.ORG_ADMIN,
          HasuraRole.SCHOOL_ADMIN,
        ]}
      >
        {groupedTags.map((tagGroup) => (
          <Flex key={tagGroup.id} direction="column" gap={2}>
            <TagGroupName
              name={tagGroup.name}
              isExternal={tagGroup.isExternal}
              fontSize="sm"
            />

            <Flex gap={2} wrap="wrap">
              {tagGroup.tags.map((tag) => (
                <Tag
                  key={tag.enrollment_period_tag.id}
                  variant="tag"
                  fontSize="xs"
                >
                  <Box whiteSpace="nowrap">
                    {tag.enrollment_period_tag.name}
                  </Box>
                </Tag>
              ))}
              {(isAvelaOrgAdmin || !tagGroup.isExternal) && (
                <WithUserPermissions permissions={formSchoolTagPermissions}>
                  <SingleTagsPopover
                    enrollmentPeriodId={enrollmentPeriodId}
                    tagGroupId={tagGroup.id}
                    formSchoolId={{
                      formSchoolRankId: schoolRank.id,
                      formId: schoolRank.form.id,
                      schoolId: schoolRank.school.id,
                    }}
                    refetchQueries={[GET_SCHOOLS_RANK_VIEW]}
                  />
                </WithUserPermissions>
              )}
            </Flex>
          </Flex>
        ))}
      </WithRequiredHasuraRoles>
    </Flex>
  );
};
